import React, { useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { Button, Col, DatePicker, Form, Row, Select, Space, Table } from 'antd';
import dayjs from 'dayjs';
import { SocketContext } from '../../../App';
import { showCustomNotification, useNotificationInstance } from '../../../utils/extend/AntdNotificationEx';
import fetchApi from '../../../utils/api/fetchApi';
import systemConfig from '../../../config/systematic';
import DownloadExcel from '../../report/DownloadExcel';
import antMessage from '../../../utils/extend/AntdNotification';
import getList from '../../universal/getList';

const { RangePicker } = DatePicker;

// ==================== 类型定义 ====================

interface RawRow {
    roomId: string;
    roomNum?: string;
    roomType?: string;
    date: string; // 'YYYY-MM-DD'
    totalHeadCount: number;
    counts?: number;
    reCounts?: number;
    [key: string]: any;
}

interface DailyData {
    [date: string]: number;
}

interface ProcessedRow {
    key: string;
    roomId: string;
    roomName: string;
    totalHeadCount: number;
    dailyData: DailyData;
}

/**
 * ✅ Excel 导出专用：扁平化行数据
 */
interface ExportRow {
    key: string;
    roomId: string;
    roomName: string;
    totalHeadCount: number;
    [date: string]: string | number;
}

/**
 * ✅ Excel 专用列配置
 */
interface ExcelColumn {
    header: string;
    key: string;
    width: number;
    isDateColumn?: boolean;
}

// ==================== 常量 ====================

const COL_WIDTH = {
    serial: 60,
    roomNum: 80,
    roomType: 100,
    counts: 80,
    reCounts: 80,
    totalHeadCount: 80,
    date: 80,
};

// ==================== 工具函数 ====================

function extractSortedDates(rawData: RawRow[]): string[] {
    if (!rawData?.length) return [];
    return Array.from(new Set(rawData.map(item => item.date))).sort();
}

function transformToPivotTable(rawData: RawRow[]): {
    rows: ProcessedRow[];
    dates: string[];
} {
    if (!rawData?.length) {
        return { rows: [], dates: [] };
    }

    const dates = extractSortedDates(rawData);

    const grouped = new Map<string, RawRow[]>();
    for (const item of rawData) {
        const list = grouped.get(item.roomId);
        if (list) {
            list.push(item);
        } else {
            grouped.set(item.roomId, [item]);
        }
    }

    const rows: ProcessedRow[] = [];

    for (const [roomId, records] of grouped) {
        const first = records[0];

        let totalHeadCount = 0;
        const dailyData: DailyData = {};

        for (const r of records) {
            const head = r.totalHeadCount || 0;
            totalHeadCount += head;
            dailyData[r.date] = head;
        }

        rows.push({
            key: roomId,
            roomId,
            roomName: first.roomName || roomId,
            totalHeadCount,
            dailyData,
        });
    }

    return { rows, dates };
}

/**
 * ✅ 将交叉表数据转换为 ExcelJS 可直接导出的扁平行
 */
function prepareExcelRows(
    rows: ProcessedRow[],
    dates: string[]
): ExportRow[] {
    return rows.map(row => {
        const exportRow: ExportRow = {
            key: row.key,
            roomId: row.roomId,
            roomName: row.roomName,
            totalHeadCount: row.totalHeadCount,
        };

        for (const date of dates) {
            exportRow[date] = dailyDataValue(row.dailyData, date);
        }

        return exportRow;
    });
}

/**
 * ✅ 安全取值，避免 NaN / undefined
 */
function dailyDataValue(
    dailyData: DailyData | undefined,
    date: string
): number {
    if (!dailyData) return 0;
    const value = dailyData[date];
    return typeof value === 'number' ? value : 0;
}

/**
 * ✅ 生成 Excel 专用列
 */
function buildExcelColumns(dates: string[]): ExcelColumn[] {
    return [
        {
            header: '房名',
            key: 'roomName',
            width: 18,
        },
        ...dates.map(date => ({
            header: dayjs(date).format('MM月DD日'),
            key: date,
            width: 12,
            isDateColumn: true as const,
        })),
       
    ];
}

// ==================== 组件 ====================

export default function RoomPlatformStatistics(props: any) {
    const wmService: any = systemConfig.wmService;
    const [form] = Form.useForm();

    const [rows, setRows] = useState<ProcessedRow[]>([]);
    const [allDates, setAllDates] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [userName, setUserName] = useState<string>('');
    const [roomData, setRoomData] = useState<Array<any>>();

    const useContextData = useContext(SocketContext);
    const { contextData, setContextData } = useContextData;
    const notificationApi = useNotificationInstance();

    // ==================== Table 列（保持原逻辑不变） ====================

    const columns = useMemo(() => {
        const baseCols = [
            {
                title: '房名',
                dataIndex: 'roomName',
                width: 15,
                key: 'roomName',
                fixed: 'left' as const,
            },
        ];

        const dateCols = allDates.map(date => ({
            title: dayjs(date).format('MM月DD日'),
            dataIndex: ['dailyData', date] as [string, string],
            key: date,
            width: 10,
            align: 'center' as const,
            render: (val: number) => (val > 0 ? `${val}人` : '-'),
        }));

        return [...baseCols, ...dateCols];
    }, [allDates]);

    // ==================== ✅ Excel 导出专用数据 ====================

    const exportRows = useMemo(
        () => prepareExcelRows(rows, allDates),
        [rows, allDates]
    );

    const exportColumns = useMemo(
        () => buildExcelColumns(allDates),
        [allDates]
    );

    // ==================== 数据请求 ====================

    const initArgument = useCallback(async () => {
        try {
            fetchApi.GET(
                wmService,
                {
                    apiId: 'room',
                    apiExtend: 'showList',
                    apiData: {
                        branchStoreId: contextData?.currentBranchStoreId,
                        extendObj: {
                            menuHeadsIdNum: -1,
                            printersIdNum: -1,
                        },
                        mountDataOptions: {
                            userMount: {
                                isDept: true,
                            },
                        },
                    },
                }
            ).then(res => {
                if (res.success) {
                    setRoomData(res.data);
                }
            });
        } catch (error) {
            console.error('[initArgument] 初始化参数失败:', error);
        } finally {
            setContextData({ ...contextData, refresh: false });
        }
    }, [wmService, contextData, setContextData]);

    const onFinish = useCallback(async () => {
        const branchStoreId = contextData?.currentBranchStoreId;
        if (!branchStoreId) {
            antMessage('error', '错误', '必须选择分店');
            return;
        }

        setIsLoading(true);
        try {
            const values = await form.validateFields();

            const res: any = await fetchApi.GET(wmService, {
                apiId: 'financeReport',
                apiExtend: 'roomPlatNumber',
                apiData: { ...values, branchStoreId },
            });

            if (res?.success) {
                setUserName(res.data?.userName || '');
                const { rows: processedRows, dates } = transformToPivotTable(
                    res.data?.rows || []
                );
                setAllDates(dates);
                setRows(processedRows);
            } else {
                showCustomNotification(notificationApi!, {
                    type: 'error',
                    message: '请求失败',
                    description: res?.message || '未知错误',
                });
            }
        } catch (error: any) {
            if (error?.errorFields) {
                showCustomNotification(notificationApi!, {
                    type: 'warning',
                    message: '数据校验失败',
                    description: '请输入所需数据',
                });
            } else {
                showCustomNotification(notificationApi!, {
                    type: 'error',
                    message: '请求异常',
                    description: error?.message || String(error),
                });
            }
        } finally {
            setIsLoading(false);
        }
    }, [wmService, contextData, form, notificationApi]);

    // ==================== 生命周期 ====================

    useEffect(() => {
        form.setFieldsValue({
            chooseTime: [dayjs(), dayjs()],
        });
    }, [form]);

    useEffect(() => {
        if (contextData?.currentBranchStoreId) {
            initArgument();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contextData?.currentBranchStoreId]);

    // ==================== 渲染 ====================

    return (
        <div style={{ padding: 20 }}>
            <Form form={form} onFinish={onFinish} layout="inline">
                <Row gutter={16} align="middle" style={{ width: '100%' }}>
                    <Col span={8}>
                        <Form.Item
                            label="日期"
                            name="chooseTime"
                            rules={[{ required: true, message: '请选择日期' }]}
                        >
                            <RangePicker format="YYYY-MM-DD" style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>

                    <Col span={16}>
                        <Form.Item label="房间" name="roomIds">
                            <Select
                                mode="multiple"
                                allowClear
                                showSearch
                                placeholder="请选择房间!"
                                optionFilterProp="children"
                                onSearch={(e) =>
                                    getList.search(
                                        'room',
                                        setRoomData,
                                        e,
                                        {
                                            andWhereObj: {
                                                branchStoreId:
                                                    contextData?.currentBranchStoreId,
                                            },
                                        }
                                    )
                                }
                                filterOption={() => true}
                            >
                                {getList.general(roomData, 'roomNum')}
                            </Select>
                        </Form.Item>
                    </Col>

                    <Col span={10}>
                        <Form.Item>
                            <Space>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    loading={isLoading}
                                >
                                    统计
                                </Button>

                                {/* ✅ 下载使用 Excel 专用数据 */}
                                <Button
                                    onClick={() =>
                                        DownloadExcel(
                                            exportRows,
                                            'roomPlatNumber',
                                            {
                                                date: form.getFieldValue(
                                                    'chooseTime'
                                                ),
                                                userName,
                                                columnsHead: exportColumns,
                                            }
                                        )
                                    }
                                    disabled={exportRows.length === 0}
                                >
                                    下载 EXCEL 文件
                                </Button>
                            </Space>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>

            <div style={{ marginTop: 20 }}>
                <Table
                    size="middle"
                    id="roomId"
                    pagination={false}
                    columns={columns}
                    dataSource={rows}
                    bordered
                    sticky
                    scroll={{ x: 'max-content' }}
                />
            </div>
        </div>
    );
}